"use client";

import { useEffect, useState } from "react";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaMapPin, FaPhone } from "react-icons/fa";
import { FiCheckCircle } from "react-icons/fi";
import { MdAlternateEmail } from "react-icons/md";

import TextInput from "@components/ui/form/text-input";
import SelectField from "@components/ui/form/select";
import Textarea from "@components/ui/form/textarea";
import Button from "@components/ui/form/button";
import { ContactSidebar } from "./contact-form";
import FacilityPicker from "@components/ui/facility-picker";
import { getFacilityBySlug } from "@/data/facilities";

enum Tires {
	FULL = "FULL",
	SUMMER = "SUMMER",
	WINTER = "WINTER",
}

enum Condition {
	BAD = "BAD",
	NORMAL = "NORMAL",
	GOOD = "GOOD",
}

enum Key {
	COMPLETE = "COMPLETE",
	DEVIANT = "DEVIANT",
}

const validationSchema = Yup.object().shape({
	name: Yup.string().max(50, "För långt namn").required("Fyll i ditt namn"),
	email: Yup.string().email("Ogiltig e-postadress").required("Fyll i din e-postadress"),
	phone: Yup.string().required("Fyll i ditt telefonnummer"),
	mileage: Yup.string().max(6, "Max miltal är 999 999 mil").required("Fyll i miltal"),
	registrationNumber: Yup.string()
		.required("Fyll i registreringsnummer")
		.matches(/^[A-Za-z]{3}\s{0,1}[0-9]{2}[A-Za-z0-9]{1}$/, "Registreringsnummer måste skrivas korrekt t.ex. ABC123"),
	message: Yup.string().max(1000, "För långt meddelande"),
	keySet: Yup.string().ensure().required("Välj nyckeluppsättning"),
	serviceBook: Yup.string().ensure().required("Välj servicebok"),
	tires: Yup.string<Tires>().oneOf(Object.values(Tires), "Välj däck").required("Välj däck"),
	summerTiresCondition: Yup.string().when("tires", {
		is: Tires.WINTER,
		then: (schema) => schema.optional(),
		otherwise: (schema) => schema.required("Välj skick"),
	}),
	winterTiresCondition: Yup.string().when("tires", {
		is: Tires.SUMMER,
		then: (schema) => schema.optional(),
		otherwise: (schema) => schema.required("Välj skick"),
	}),
});

const mapTires = (val: string) => {
	switch (val) {
		case Tires.FULL:
			return "Både sommar och vinterdäck";
		case Tires.SUMMER:
			return "Endast sommardäck";
		case Tires.WINTER:
			return "Endast vinterdäck";
		default:
			return "";
	}
};

const mapCondition = (val: string) => {
	switch (val) {
		case Condition.BAD:
			return "Dåliga";
		case Condition.NORMAL:
			return "Normala";
		case Condition.GOOD:
			return "Bra";
		default:
			return "";
	}
};

const mapKey = (val: string) => {
	switch (val) {
		case Key.COMPLETE:
			return "Komplett";
		case Key.DEVIANT:
			return "Avvikande";
		default:
			return "";
	}
};

interface SellCarFormProps {
	defaultRegnr?: string;
}

const SellCarForm = ({ defaultRegnr }: SellCarFormProps = {}) => {
	const {
		handleSubmit,
		formState: { errors, isSubmitting },
		register,
		watch,
	} = useForm<any>({
		defaultValues: {
			name: "",
			email: "",
			phone: "",
			registrationNumber: defaultRegnr ?? "",
			mileage: "",
			keySet: "",
			serviceBook: "",
			tires: "",
			message: "",
			summerTiresCondition: "",
			winterTiresCondition: "",
		},
		resolver: yupResolver(validationSchema),
	});

	// When the reg number is prefilled (from the homepage widget), auto-focus the
	// name field so the user continues typing without needing to click.
	useEffect(() => {
		if (!defaultRegnr) return;
		const el = document.querySelector<HTMLInputElement>('input[name="name"]');
		el?.focus();
	}, [defaultRegnr]);

	const [isSent, setIsSent] = useState(false);
	const [facilitySlug, setFacilitySlug] = useState("norrkoping");
	const tiresSelection = watch("tires");

	const onSubmit = async (values) => {
		const body = {
			...values,
			facility: facilitySlug,
			keySet: mapKey(values.keySet),
			serviceBook: mapKey(values.keySet),
			tires: mapTires(values.tires),
			summerTiresCondition:
				values.tires === Tires.FULL || values.tires === Tires.SUMMER ? mapCondition(values.summerTiresCondition) : "",
			winterTiresCondition:
				values.tires === Tires.FULL || values.tires === Tires.WINTER ? mapCondition(values.winterTiresCondition) : "",
		};

		await fetch("/api/offer", {
			method: "POST",
			body: JSON.stringify(body),
			headers: {
				"Content-Type": "application/json",
			},
		}).then(() => {
			setIsSent(true);
		});
	};
	return (
		<div className="max-w-5xl grid grid-cols-1 lg:grid-cols-5 mx-auto text-left rounded-xl border border-white/[0.06] overflow-hidden">
			<div className="col-span-3 py-8 px-6 bg-white/[0.03]">
				{!isSent ? (
					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="mb-6">
							<FacilityPicker value={facilitySlug} onChange={setFacilitySlug} label="Vart vill du sälja ditt fordon?" />
						</div>
						<div className="flex flex-col gap-4">
							<div className="space-y-2">
								<div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-4">
									<TextInput label="Ditt namn" type="text" error={errors?.name} withAsterisk {...register("name")} />
									<TextInput
										label="Din e-postadress"
										type="email"
										placeholder="@"
										error={errors?.email}
										withAsterisk
										{...register("email")}
									/>
								</div>
								<div className="grid grid-cols-1 gap-4">
									<TextInput
										label="Telefonnummer"
										type="tel"
										error={errors?.phone}
										withAsterisk
										{...register("phone")}
									/>
								</div>
							</div>
							<div className="mt-1">
								<h3>Om fordonet</h3>

								<div className="space-y-2">
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
										<TextInput
											label="Registreringsnummer"
											type="text"
											error={errors?.registrationNumber}
											placeholder="ABC123"
											max={7}
											withAsterisk
											{...register("registrationNumber")}
										/>
										<TextInput
											label="Miltal"
											type="number"
											error={errors?.mileage}
											placeholder=""
											min={0}
											max={999999}
											withAsterisk
											{...register("mileage")}
										/>
									</div>
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
										<SelectField
											label="Nyckeluppsättning"
											error={errors?.keySet}
											size="md"
											data={[
												{ value: "COMPLETE", label: "Komplett" },
												{ value: "DEVIANT", label: "Avvikande" },
											]}
											withAsterisk
											{...register("keySet")}
										/>
										<SelectField
											label="Servicebok"
											error={errors?.serviceBook}
											data={[
												{ value: "COMPLETE", label: "Komplett" },
												{ value: "DEVIANT", label: "Avvikande" },
											]}
											withAsterisk
											{...register("serviceBook")}
										/>
									</div>
									<div className="w-full">
										<SelectField
											label="Däck"
											error={errors?.tires}
											data={[
												{ value: "FULL", label: "Sommar och vinterdäck" },
												{ value: "SUMMER", label: "Endast sommardäck" },
												{ value: "WINTER", label: "Endast vinterdäck" },
											]}
											withAsterisk
											{...register("tires")}
										/>
									</div>
									<div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
										{tiresSelection && tiresSelection !== Tires.WINTER ? (
											<SelectField
												label="Skick sommardäck"
												error={errors?.summerTiresCondition}
												data={[
													{ value: "BAD", label: "Dåliga" },
													{ value: "NORMAL", label: "Normala" },
													{ value: "GOOD", label: "Bra" },
												]}
												withAsterisk
												{...register("summerTiresCondition")}
											/>
										) : null}
										{tiresSelection && tiresSelection !== Tires.SUMMER ? (
											<SelectField
												label="Skick vinterdäck"
												error={errors?.winterTiresCondition}
												data={[
													{ value: "BAD", label: "Dåliga" },
													{ value: "NORMAL", label: "Normala" },
													{ value: "GOOD", label: "Bra" },
												]}
												withAsterisk
												{...register("winterTiresCondition")}
											/>
										) : null}
									</div>
									<div className="grid grid-cols-1 gap-4">
										<Textarea
											label="Övrig info"
											error={errors?.message}
											withAsterisk={false}
											{...register("message")}
										/>
									</div>
								</div>
							</div>
						</div>

						<Button
							className="mt-8"
							variant="accent"
							full
							loading={isSubmitting}
							type="submit"
							disabled={isSubmitting || isSent}
						>
							Skicka
						</Button>
					</form>
				) : (
					<div className="flex items-center gap-3 p-5 rounded-lg bg-success/10 border border-success/20">
						<FiCheckCircle size={20} className="text-success shrink-0" />
						<div>
							<p className="font-semibold text-white text-sm">Din förfrågan har skickats!</p>
							<p className="text-xs text-gray-400 mt-0.5">Vi återkommer inom kort.</p>
						</div>
					</div>
				)}
			</div>
			<ContactSidebar facility={getFacilityBySlug(facilitySlug)} />
		</div>
	);
};

export default SellCarForm;
